<template>
  <d-container
    fluid
    class="main-content-container px-4 kpi-targets">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('kpi.workersGoals') }}</span>
        <h3 class="page-title">{{ $t('kpi.workersGoalsList') }}</h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/cele-pracownikow/nowy')">
          <i class="material-icons">add</i>
          {{ $t('kpi.addNewKpi') }}
        </d-button>
      </d-col>
    </d-row>

    <list-filters
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="loaded"
      :view-name="'kpi-targets'"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('users.worker') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 240px;">{{ $t('kpi.period') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 480px;">{{ $t('kpi.goals') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 200px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(target, index) of kpiTargets"
            :key="'target-' + index">
            <td class="align-middle">
              {{ target.user_name }}
            </td>
            <td class="align-middle">
              {{ monthName(target.month) }} {{ target.year }}
            </td>
            <td class="align-middle">
              <small>
                {{ $t('kpi.contactsNumberRegardingNewOrders') }}: <strong>{{ target.new_orders_contacts_count }}</strong><br>
                {{ $t('kpi.numberOfStartedOrders') }}: <strong>{{ target.started_orders_count }}</strong><br>
                {{ $t('kpi.amountOfStartedOrders') }}: <strong>{{ target.started_orders_amount }}</strong><br>
                {{ $t('kpi.numberOfStartedOffers') }}: <strong>{{ target.started_offers_count }}</strong><br>
                {{ $t('kpi.numberOfNewCustomers') }}: <strong>{{ target.new_customers_count }}</strong><br>
                {{ $t('kpi.numberOfNewContacts') }}: <strong>{{ target.new_contacts_count }}</strong>
              </small>
            </td>
            <td class="align-middle">
              <d-button
                theme="primary"
                @click.native="$router.push('/cele-pracownikow/edycja/' + target.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="secondary"
                @click.native="$router.push('/cele-pracownikow/nowy/duplikuj/' + target.id)"
                class="mr-2"
                :title="$t('ui.duplicate')">
                <i class="material-icons">file_copy</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeKpiTarget(target.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="kpiTargets.length === 0">
            <td
              colspan="10"
              class="text-center">
              {{ $t('kpi.noWorkersGoalsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!loaded"
        class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import CalendarUtils from './../utils/CalendarUtils.js';
import debounce from 'lodash.debounce';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import datepickerTranslations from '@/data/datepicker-translations.js';
import getPrefferedLanguage from './../utils/i18nUtils.js';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'kpi-targets',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    filtersConfig () {
      return [
        {
          key: 'user_id',
          apiKey: 'user_id',
          label: this.$t('users.worker'),
          type: 'userSelect',
          isBasic: true,
          defaultValue: null
        },
        {
          key: 'period',
          label: this.$t('kpi.period'),
          placeholder: this.$t('ui.chooseDate'),
          type: 'monthPicker',
          isBasic: true,
          defaultValue: null,
          language: datepickerTranslations[getPrefferedLanguage()],
          apiTransform: (val, where) => {
            if (val) {
              let date = new Date(val);
              where.year = date.getFullYear();
              where.month = date.getMonth() + 1;
            }
          }
        }
      ];
    }
  },
  data () {
    return {
      kpiTargets: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {},
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let whereData = cloneDeep(this.apiQueryPayload) || {};

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/kpi/targets/items?page=' + this.currentPage,
        listField: 'kpiTargets',
        loadError: 'loadError',
        params: {
          where: whereData,
          return: 'paginate'
        }
      });
    },
    removeKpiTarget (targetID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/kpi/targets/delete/',
        id: targetID,
        successTitle: this.$t('kpi.removeGoalSuccessTitle'),
        successText: this.$t('kpi.removeGoalSuccessText'),
        confirmTitle: this.$t('kpi.removeGoalConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('kpi.removeGoalErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    monthName (monthNumber) {
      return CalendarUtils.getMonthName(monthNumber);
    }
  }
};
</script>
